import { db } from "../firebaseConfig";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp, query, where, orderBy, limit, getDocs, startAfter, getDoc
} from "firebase/firestore";

/** utils */
const slugify = (s) =>
  (s || "").toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const normalize = (s) =>
  (s || "").toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ").trim();

/** create */
export async function addProduct(data) {
  const slug = data.slug?.trim() || slugify(data.title);
  const search = Array.from(new Set([
    normalize(data.title),
    data.brandId || "",
    ...(data.variants || []).flatMap(v => [v.color?.toLowerCase(), v.size?.toLowerCase()])
  ].filter(Boolean)));

  const payload = {
    ...data,
    slug,
    search,
    status: data.status || "active",
    price: Number.isFinite(Number(data.price)) ? Math.round(Number(data.price)) : 0,
    stock: Number.isFinite(Number(data.stock)) ? Math.max(0, Math.round(Number(data.stock))) : 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const ref = await addDoc(collection(db, "products"), payload);
  return ref.id;
}

/** update */
export async function updateProduct(id, patch) {
  const payload = { ...patch, updatedAt: serverTimestamp() };
  if (patch.title && !patch.slug) payload.slug = slugify(patch.title);
  if (typeof patch.price !== "undefined") payload.price = Math.round(Number(patch.price) || 0);
  if (typeof patch.stock !== "undefined") payload.stock = Math.max(0, Math.round(Number(patch.stock) || 0));
  await updateDoc(doc(db, "products", id), payload);
}

/** delete */
export async function deleteProductById(id) {
  await deleteDoc(doc(db, "products", id));
}

/** get one */
export async function getProduct(id) {
  const snap = await getDoc(doc(db, "products", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * list (paginée)
 * @param {Object} opts
 * @param {number} opts.pageSize - nb d’éléments par page
 * @param {any} opts.cursor - DocumentSnapshot retourné par l’appel précédent
 * @param {boolean} opts.forAdmin - si true, pas de filtre par status
 * @param {string} [opts.status="active"] - filtre status si forAdmin=false
 * @param {string|null} [opts.brandId=null] - filtre marque
 * @param {string|null} [opts.categoryId=null] - filtre catégorie
 */
export async function listProducts({
  pageSize = 10,
  cursor = null,
  forAdmin = false,
  status = "active",
  brandId = null,
  categoryId = null
} = {}) {
  const base = collection(db, "products");

  // Contrainte de base: tri par createdAt desc (index conseillé)
  const constraints = [];

  if (!forAdmin) {
    constraints.push(where("status", "==", status));
  }
  if (brandId) {
    constraints.push(where("brandId", "==", brandId));
  }
  if (categoryId) {
    constraints.push(where("categoryId", "==", categoryId));
  }

  constraints.push(orderBy("createdAt", "desc"));
  constraints.push(limit(pageSize));

  let q = query(base, ...constraints);
  if (cursor) q = query(q, startAfter(cursor));

  const snap = await getDocs(q);
  const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const nextCursor = snap.docs.at(-1) || null;

  return { items, nextCursor };
}
